import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import styled from 'styled-components';
import Color from '../../../enums/color';
import Pagination from './Pagination';
import { TABLE_SCROLL_DIFFERENCE, TABLE_SELECTOR } from '../../../constants/shared';
import SortDirection from '../../../enums/sortDirection';
import TableActions from './TableActions';
import store from '../../../store';
import useGlobalState from '../../../hooks/useGlobalState';
import debounce from 'lodash.debounce';
import StyledTable from './StyledTable';
import SortableHeaderCell from './SortableHeaderCell';
import EmptyTable from './EmptyTable';
import { getFormattedColumns } from '../../../helpers/table';
import Label from '../Label';
const TableContainer = styled.div `
  margin-top: 16px;
  padding-bottom: 16px;
  background: ${Color.White};
`;
const locale = {
    emptyText: <EmptyTable />
};
const Table = ({ columns, tableConfig, onAddItemAction, className, filterPopoverContent }) => {
    var _a;
    const [scroll, setScroll] = useState(0);
    const tableStore = store.table[tableConfig.id];
    useLayoutEffect(() => {
        var _a;
        if (tableConfig.tableHeight)
            return;
        const tableOuterHeight = (_a = tableConfig.tableOuterHeight) !== null && _a !== void 0 ? _a : TABLE_SCROLL_DIFFERENCE;
        const table = document.querySelector(TABLE_SELECTOR);
        const scrollValue = document.body.clientHeight - table.getBoundingClientRect().top - tableOuterHeight;
        setScroll(scrollValue);
    });
    useEffect(() => {
        tableStore.initialize(tableConfig);
    }, []);
    const tableState = useGlobalState(tableStore);
    useEffect(() => {
        tableConfig.fetchData();
        return () => {
            tableStore.reset();
        };
    }, []);
    const handleSort = (key) => {
        let newDirection = tableState.sort.direction === SortDirection.Ascending ?
            SortDirection.Descending : SortDirection.Ascending;
        if (key !== tableState.sort.key) {
            newDirection = SortDirection.Ascending;
        }
        tableStore.updateSort({ key: key, direction: newDirection });
        tableConfig.fetchData();
    };
    const handlePageSizeChange = (pageSize) => {
        tableStore.updatePagination({ pageNumber: 1, pageSize: pageSize });
        tableConfig.fetchData();
    };
    const handlePageNumberChange = (pageNumber) => {
        tableStore.updatePagination({ pageNumber: pageNumber, pageSize: tableState.pagination.pageSize });
        tableConfig.fetchData();
    };
    const delayedSearch = useCallback(debounce(() => {
        tableStore.resetPageNumber();
        tableConfig.fetchData();
    }, 500), []);
    const handleSearch = (value) => {
        tableStore.updateSearchValue(value);
        delayedSearch();
    };
    const getColumnTitle = (column) => {
        const { name, sortable, key, automationId } = column;
        const sortableHeaderCell = <SortableHeaderCell title={name} isActive={tableState.sort.key === key} direction={tableState.sort.direction} automationId={automationId} onClick={() => handleSort(key)}/>;
        if (sortable)
            return sortableHeaderCell;
        const isReactElement = React.isValidElement(name);
        return isReactElement ? name : <Label title={name} bolded automationId={automationId}/>;
    };
    return (<>
      <TableActions config={tableConfig.actionsConfig} searchValue={tableState.searchValue} handleSearch={handleSearch} handleAddItemAction={onAddItemAction} handleExportData={tableConfig.exportData} filterPopoverContent={filterPopoverContent}/>
      <TableContainer className={className}>
        <StyledTable dataSource={tableState.items} columns={getFormattedColumns(columns, getColumnTitle)} loading={tableState.isLoading} pagination={false} locale={locale} scroll={{ y: (_a = tableConfig.tableHeight) !== null && _a !== void 0 ? _a : scroll }} scrollableTableWidth={tableConfig.scrollableTableWidth}/>
        <Pagination totalRecords={tableState.totalRecords} onPageNumberChange={handlePageNumberChange} onPageSizeChange={handlePageSizeChange} pageSize={tableState.pagination.pageSize} pageNumber={tableState.pagination.pageNumber} isDisabled={tableState.isLoading}/>
      </TableContainer>
    </>);
};
export default Table;
